import { Box } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useIsMutating } from '@tanstack/react-query'

import { isApiError } from '@/types/apiErrors.types'

import { LoginRoutePathsEnum } from '@/modules/login/routes/loginRouteConfig.types'
import { PageRoutePathsEnum } from '@/modules/page/routes/pageRouteConfig.types'
import { registerFormSchema } from '@/modules/register/components/RegisterForm/RegisterForm.schema'
import type { RegisterFormSchema } from '@/modules/register/components/RegisterForm/RegisterForm.types'
import { registerFormDefaultValues } from '@/modules/register/components/RegisterForm/RegisterForm.values'

import { Anchor } from '@/components/Anchor/Anchor'
import { Button } from '@/components/Button/Button'
import { CheckboxInput } from '@/components/CheckboxInput/CheckboxInput'
import { PasswordInput } from '@/components/PasswordInput/PasswordInput'
import { Text } from '@/components/Text/Text'
import { TextInput } from '@/components/TextInput/TextInput'

import { FORM_MESSAGES_MAP } from '@/constants/formMessages.constants'

import { useRolanWealthServiceAuthControllerRegister } from '@/__generated__/queries'

export function RegisterForm({
  onUserCreated,
}: {
  onUserCreated: (isUserCreated: boolean) => void
}) {
  const isMutating = useIsMutating({
    exact: true,
  })

  const form = useForm<RegisterFormSchema>({
    initialValues: registerFormDefaultValues,
    validate: zodResolver(registerFormSchema),
  })

  const registerMutation = useRolanWealthServiceAuthControllerRegister({
    onError: error => {
      let errorMessage = FORM_MESSAGES_MAP.UNKNOWN_ERROR

      if (isApiError(error)) {
        errorMessage = error.body.message || errorMessage
      }

      notifications.show({
        color: 'red',
        message: errorMessage,
        title: 'Registration failed',
      })
    },
    onSuccess: () => {
      onUserCreated(true)

      form.reset()
    },
  })

  const handleSubmit = form.onSubmit(values => {
    registerMutation.mutate({
      requestBody: values,
    })
  })

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Box mt={48}>
        <TextInput
          label="First Name"
          placeholder="Enter your name"
          {...form.getInputProps('firstName')}
        />

        <TextInput
          label="Last Name"
          placeholder="Enter your last name"
          {...form.getInputProps('lastName')}
        />

        <TextInput
          label="Email"
          placeholder="Enter your email address"
          {...form.getInputProps('email')}
        />

        <PasswordInput
          label="Password"
          placeholder="Enter your password"
          {...form.getInputProps('password')}
        />

        <PasswordInput
          label="Confirm Password"
          placeholder="Confirm your password"
          {...form.getInputProps('confirmPassword')}
        />

        <CheckboxInput
          label={
            <Text>
              I agree to the{' '}
              <Anchor
                href={PageRoutePathsEnum.TERMS_CONDITIONS}
                target="_blank"
                variant="primary"
              >
                Terms & Conditions
              </Anchor>
            </Text>
          }
          mb={16}
          {...form.getInputProps('agreeToTerms')}
        />

        <CheckboxInput
          label={
            <Text>
              I agree to the{' '}
              <Anchor
                href={PageRoutePathsEnum.PRIVACY_POLICY}
                target="_blank"
                variant="primary"
              >
                Privacy Policy
              </Anchor>
            </Text>
          }
          {...form.getInputProps('agreeToPrivacyPolicy')}
        />
      </Box>

      <Box mt={56}>
        <Button loading={!!isMutating} type="submit">
          Sign up
        </Button>
      </Box>

      <Box mt={24}>
        <Text>
          Already have account?{' '}
          <Anchor href={LoginRoutePathsEnum.LOGIN} variant="primary">
            Log in
          </Anchor>
        </Text>
      </Box>
    </Box>
  )
}
